<template>
  <div class="profitable-card">
    <h3>Top Products by Gross Profit</h3>
    <div v-if="topProducts.length > 0">
      <table class="products-table">
        <thead>
          <tr>
            <th>Product</th>
            <th>Revenue</th>
            <th>COGS%</th>
            <th>Gross Profit</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="p in topProducts" :key="p.slug">
            <td>{{ p.name }}</td>
            <td><CurrencyFormatter :value="p.revenue" :currency="companySettings.currency" /></td>
            <td>{{ p.cogsPercent > 0 ? p.cogsPercent + "%" : "—" }}</td>
            <td class="profit"><CurrencyFormatter :value="p.grossProfit" :currency="companySettings.currency" /></td>
          </tr>
        </tbody>
      </table>
    </div>
    <p v-else class="empty">No sales data for today</p>
  </div>
</template>

<script>
import { api, dayRange } from "@monolith/helpers";
import { mapGetters } from "vuex";
import { CurrencyFormatter } from "@monolith/components";
import { cogsMixin } from "@/components/COGS/shared/cogsMixin";

export default {
  name: "ProfitableProductsCard",
  components: { CurrencyFormatter },
  mixins: [cogsMixin],
  props: {
    site: { type: String, required: true },
  },
  data() {
    return {
      topProducts: [],
    };
  },
  watch: {
    site() {
      this.loadData();
    },
    cogsMenuRecipes() {
      this.loadData();
    },
  },
  mounted() {
    this.loadData();
  },
  methods: {
    async loadData() {
      if (!this.site || !this.companyName) return;

      const today = dayRange.getToday();
      const productData = await api.get_products_stacked_data(
        this.companyName,
        this.site,
        today[0],
        today[1]
      );

      if (!productData || productData.length === 0) {
        this.topProducts = [];
        return;
      }

      // Aggregate revenue per product slug
      const revenueBySlug = {};
      productData.forEach((row) => {
        const slug = row.key[2];
        revenueBySlug[slug] = (revenueBySlug[slug] || 0) + row.value;
      });

      // Combine with COGS data
      const products = Object.entries(revenueBySlug).map(([slug, revenue]) => {
        const [name, found, percentage] = this.checkMenuRecipe(slug, this.site);
        const cogsPercent = found && percentage ? parseFloat(percentage) : 0;
        const grossProfit = revenue * (1 - cogsPercent / 100);
        return { slug, name, revenue, cogsPercent, grossProfit };
      });

      // Sort by gross profit descending, take top 10
      products.sort((a, b) => b.grossProfit - a.grossProfit);
      this.topProducts = products.slice(0, 10);
    },
  },
};
</script>

<style scoped lang="scss">
.profitable-card {
  padding: 1rem;
  height: 100%;
  box-sizing: border-box;
  overflow: auto;

  h3 {
    margin: 0 0 0.75rem;
    font-size: 1rem;
    text-transform: uppercase;
    color: var(--action-colour);
  }

  .products-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.85rem;

    th {
      text-align: left;
      padding: 0.3rem 0.25rem;
      border-bottom: 1px solid var(--action-colour);
      text-transform: uppercase;
      font-size: 0.75rem;
      opacity: 0.7;
    }

    td {
      padding: 0.3rem 0.25rem;
      border-bottom: 1px solid rgba(255, 255, 255, 0.05);
    }

    .profit {
      font-weight: bold;
      color: var(--action-colour);
    }
  }

  .empty {
    font-size: 0.9rem;
    opacity: 0.6;
  }
}
</style>
